import { useId } from 'react';

import { Box } from '@chakra-ui/react';

import { HERO_TAKEOVER_DESKTOP_ART_WIDTH } from '../constants/hero-content';

const GOLD = '#EDA93B';

/**
 * Separator between repetitions — a middot padded by two spaces either side, as authored in Figma.
 * Non-breaking spaces because SVG collapses runs of regular whitespace, which would halve the gap.
 */
const BADGE_SEPARATOR = '\u00a0\u00a0·\u00a0\u00a0';

/**
 * Mean glyph advance for Barlow Condensed 500 uppercase, as a fraction of `fontSize`.
 * Tuned slightly short of the real average so ceil() still closes the ring; `textLength` then
 * tightens the join.
 */
const BARLOW_CONDENSED_UPPERCASE_ADVANCE_EM = 0.4;

type TextRing = {
  /** Radius of the baseline circle the glyphs sit on; they grow outward from it. */
  radius: number;
  opacity: number;
  /** Gaussian blur applied to this ring alone. Omitted on the crisp foreground ring. */
  blurStdDeviation?: number;
};

type BadgeGeometry = {
  viewBox: string;
  center: { x: number; y: number };
  /** `objectPosition` equivalent for the artwork inside its container. */
  preserveAspectRatio: string;
  border: { x: number; y: number; size: number; radius: number; strokeWidth: number };
  innerCircle: { radius: number; strokeWidth: number };
  /** The two overlapping triangles that form the Udacity "masters symbol". */
  triangles: { d: string; opacity: number }[];
  /** The bright, unblurred ring inside the badge border. */
  foregroundRing: TextRing & { fontSize: number; letterSpacingRatio: number };
  /** Faint oversized rings behind the badge, each blurred by its own amount. */
  backgroundRings: TextRing[];
  backgroundRingFontSize: number;
  backgroundRingLetterSpacingRatio: number;
};

/**
 * Geometry lifted 1:1 from the Figma badge artwork (`bts` frame, node 1855:1335 desktop /
 * 1856:2245 mobile): ring radii, font sizes, letter spacing, opacities and the masters-symbol
 * paths all match the design. The mobile artwork is the desktop artwork at half scale in a
 * different viewport box.
 *
 * Ring text inherits Barlow Condensed from the wrapping Box's Chakra `condensed` token. Do not put
 * the next/font family on the SVG directly (a `className`, a `fontFamily` attribute, or an inline
 * `<style>`): the server and client bundles can emit different next/font hashes, and React keeps
 * the server's attribute on hydration, leaving a family with no `@font-face` so the rings fall
 * back to Inter.
 */
const DESKTOP: BadgeGeometry = {
  viewBox: '0 0 815 572',
  center: { x: 547.02, y: 250 },
  preserveAspectRatio: 'xMaxYMid meet',
  border: { x: 388.27, y: 91.25, size: 317.5, radius: 158.75, strokeWidth: 2.5 },
  innerCircle: { radius: 108, strokeWidth: 2.5 },
  triangles: [
    { d: 'M607.289 309.734L487.289 309.734L487.289 189.734L607.289 309.734Z', opacity: 0.4 },
    { d: 'M487.279 309.734L607.279 309.734L607.279 189.734L487.279 309.734Z', opacity: 0.5 },
  ],
  foregroundRing: { radius: 125.6, opacity: 1, fontSize: 21.6, letterSpacingRatio: 0.03 },
  backgroundRings: [
    // Left unblurred so the copy stays readable; its low opacity is what pushes it back.
    { radius: 241.6, opacity: 0.24 },
    { radius: 385.6, opacity: 0.16, blurStdDeviation: 6.4 },
    { radius: 518.4, opacity: 0.08, blurStdDeviation: 6.4 },
  ],
  backgroundRingFontSize: 22.4,
  backgroundRingLetterSpacingRatio: 0.04,
};

const MOBILE: BadgeGeometry = {
  viewBox: '0 0 370 434',
  center: { x: 96, y: 160 },
  preserveAspectRatio: 'xMinYMin meet',
  border: { x: 16.625, y: 80.625, size: 158.75, radius: 79.375, strokeWidth: 1.25 },
  innerCircle: { radius: 54, strokeWidth: 1.25 },
  triangles: [
    { d: 'M126.135 189.867L66.1347 189.867L66.1347 129.867L126.135 189.867Z', opacity: 0.4 },
    { d: 'M66.1301 189.867L126.13 189.867L126.13 129.867L66.1301 189.867Z', opacity: 0.5 },
  ],
  foregroundRing: { radius: 62.8, opacity: 1, fontSize: 10.8, letterSpacingRatio: 0.03 },
  backgroundRings: [
    { radius: 120.8, opacity: 0.24 },
    { radius: 192.8, opacity: 0.16, blurStdDeviation: 3.2 },
    { radius: 259.2, opacity: 0.08, blurStdDeviation: 3.2 },
  ],
  backgroundRingFontSize: 11.2,
  backgroundRingLetterSpacingRatio: 0.04,
};

/**
 * Full circle starting at 12 o'clock and running clockwise. The clockwise direction is what puts
 * the glyphs' baseline on the circle with their ascenders pointing outward, so the text reads
 * upright across the top of the ring.
 */
function circlePath(cx: number, cy: number, r: number): string {
  return `M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} A ${r} ${r} 0 0 1 ${cx} ${cy - r}`;
}

function ringText(phrase: string, repetitions: number): string {
  return `${phrase.toUpperCase()}${BADGE_SEPARATOR}`.repeat(repetitions);
}

/**
 * How many phrase+separator units are needed to wrap `radius` without a gap.
 */
function getClosedRingRepetitions({
  fontSize,
  letterSpacingRatio,
  phrase,
  radius,
}: {
  fontSize: number;
  letterSpacingRatio: number;
  phrase: string;
  radius: number;
}): number {
  const unitCharCount = phrase.toUpperCase().length + BADGE_SEPARATOR.length;
  const advance = fontSize * (BARLOW_CONDENSED_UPPERCASE_ADVANCE_EM + letterSpacingRatio);
  const circumference = 2 * Math.PI * radius;
  return Math.max(1, Math.ceil(circumference / (unitCharCount * advance)));
}

/**
 * Degrees to rotate a ring so the first phrase is centred on 12 o'clock rather than starting there,
 * matching the design. Rotating the group is what makes this possible — text on a path cannot start
 * at a negative offset, and starting near 100% would simply run off the end of the path.
 */
function centeringRotation(phrase: string, repetitions: number): number {
  const unitLength = phrase.length + BADGE_SEPARATOR.length;
  const phraseDegrees = (360 * (phrase.length / unitLength)) / repetitions;
  return -phraseDegrees / 2;
}

function TextRingGroup({
  center,
  fontSize,
  idPrefix,
  letterSpacingRatio,
  phrase,
  repetitions,
  ring,
}: {
  center: { x: number; y: number };
  fontSize: number;
  idPrefix: string;
  letterSpacingRatio: number;
  phrase: string;
  repetitions: number;
  ring: TextRing;
}) {
  // Stretch the repeated run to the exact circumference so the ring closes seamlessly instead of
  // leaving a gap or clipping mid-word. `spacing` only adjusts gaps, never glyph shapes.
  // `textLength` belongs on `<text>` (SVG 1.1); on `<textPath>` Chrome often ignores it.
  const circumference = 2 * Math.PI * ring.radius;
  return (
    <g
      filter={ring.blurStdDeviation ? `url(#${idPrefix}-blur-${ring.radius})` : undefined}
      opacity={ring.opacity}
      transform={`rotate(${centeringRotation(phrase, repetitions)} ${center.x} ${center.y})`}
    >
      <text
        fill={GOLD}
        fontSize={fontSize}
        fontWeight={500}
        lengthAdjust='spacing'
        letterSpacing={fontSize * letterSpacingRatio}
        textLength={circumference}
      >
        <textPath
          href={`#${idPrefix}-ring-${ring.radius}`}
          lengthAdjust='spacing'
          startOffset={0}
          textLength={circumference}
        >
          {ringText(phrase, repetitions)}
        </textPath>
      </text>
    </g>
  );
}

function BadgeArtwork({
  foregroundRingRepetitions,
  geometry,
  phrase,
}: {
  foregroundRingRepetitions: number;
  geometry: BadgeGeometry;
  phrase: string;
}) {
  const idPrefix = useId().replace(/:/g, '');
  const { x: cx, y: cy } = geometry.center;
  const allRings = [geometry.foregroundRing, ...geometry.backgroundRings];

  return (
    <svg
      fill='none'
      height='100%'
      preserveAspectRatio={geometry.preserveAspectRatio}
      viewBox={geometry.viewBox}
      width='100%'
      aria-hidden
    >
      <defs>
        {allRings.map((ring) => (
          <path
            key={ring.radius}
            d={circlePath(cx, cy, ring.radius)}
            id={`${idPrefix}-ring-${ring.radius}`}
            pathLength={2 * Math.PI * ring.radius}
          />
        ))}
        {geometry.backgroundRings.map((ring) => (
          <filter key={ring.radius} id={`${idPrefix}-blur-${ring.radius}`}>
            <feGaussianBlur stdDeviation={ring.blurStdDeviation} />
          </filter>
        ))}
      </defs>

      {geometry.backgroundRings.map((ring) => (
        <TextRingGroup
          key={ring.radius}
          center={geometry.center}
          fontSize={geometry.backgroundRingFontSize}
          idPrefix={idPrefix}
          letterSpacingRatio={geometry.backgroundRingLetterSpacingRatio}
          phrase={phrase}
          ring={ring}
          repetitions={getClosedRingRepetitions({
            fontSize: geometry.backgroundRingFontSize,
            letterSpacingRatio: geometry.backgroundRingLetterSpacingRatio,
            phrase,
            radius: ring.radius,
          })}
        />
      ))}

      <rect
        height={geometry.border.size}
        rx={geometry.border.radius}
        stroke={GOLD}
        strokeWidth={geometry.border.strokeWidth}
        width={geometry.border.size}
        x={geometry.border.x}
        y={geometry.border.y}
      />
      {geometry.triangles.map((triangle) => (
        <path key={triangle.d} d={triangle.d} fill={GOLD} opacity={triangle.opacity} />
      ))}
      <circle
        cx={cx}
        cy={cy}
        opacity={0.4}
        r={geometry.innerCircle.radius}
        stroke={GOLD}
        strokeWidth={geometry.innerCircle.strokeWidth}
      />

      <TextRingGroup
        center={geometry.center}
        fontSize={geometry.foregroundRing.fontSize}
        idPrefix={idPrefix}
        letterSpacingRatio={geometry.foregroundRing.letterSpacingRatio}
        phrase={phrase}
        repetitions={foregroundRingRepetitions}
        ring={geometry.foregroundRing}
      />
    </svg>
  );
}

export function HeroTakeoverBadge({
  foregroundRingRepetitions,
  phrase,
}: {
  foregroundRingRepetitions: number;
  phrase: string;
}) {
  return (
    <>
      <Box
        display={{ base: 'block', md: 'none' }}
        fontFamily='condensed'
        inset={0}
        maxWidth='369px'
        position='absolute'
      >
        <BadgeArtwork foregroundRingRepetitions={foregroundRingRepetitions} geometry={MOBILE} phrase={phrase} />
      </Box>
      <Box
        bottom={0}
        display={{ base: 'none', md: 'block' }}
        fontFamily='condensed'
        position='absolute'
        right={0}
        top={0}
        width={HERO_TAKEOVER_DESKTOP_ART_WIDTH}
      >
        <BadgeArtwork foregroundRingRepetitions={foregroundRingRepetitions} geometry={DESKTOP} phrase={phrase} />
      </Box>
    </>
  );
}
